import type { MessageKey } from '@ralph/contracts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * What the auth domain's refusals say, in Brazilian Portuguese (checked against the English
 * shape at compile time).
 *
 * These are the first Portuguese sentences a person ever reads here — the sign-in page paints
 * before any session exists — so the register is set by them: `você`, never `o senhor`, and
 * *pessoa administradora* rather than assuming a gender for somebody the platform has never
 * met (ADR-0018).
 *
 * `tenant` stays English: it is the word Entra itself puts on the screen an administrator has
 * open beside this one (D-15).
 */
export const auth: Pick<Messages['errors'], Extract<MessageKey, `auth.${string}`>> = {
  // --- signing in ----------------------------------------------------------

  'auth.local_sign_in_disabled': () => 'A entrada por senha não está ativada nesta instalação.',
  'auth.sign_in_required': () => 'Entre para mudar suas preferências.',
  'auth.sign_in_failed': () => 'Não foi possível concluir sua entrada. Comece de novo.',
  'auth.sign_in_session_expired': () => 'Esta entrada demorou demais ou já foi usada. Comece de novo.',
  'auth.incorrect_password': () => 'Essa senha não está correta.',
  'auth.provider_required': () => 'Escolha uma forma de entrar.',
  'auth.provider_not_configured': ({ provider }) =>
    `Não existe forma de entrar chamada “${provider}” nesta instalação.`,
  'auth.provider_response_unusable': () =>
    'Seu provedor de identidade não devolveu tudo que esta plataforma precisa para identificar você. ' +
    'Peça para uma pessoa administradora revisar a configuração de entrada.',
  'auth.tenant_not_permitted': ({ tenant }) => `Não é permitido entrar pelo tenant ${tenant} aqui.`,
  'auth.email_domain_not_permitted': ({ domains }) => `A entrada aqui é limitada a ${domains}.`,
  'auth.account_deactivated': () => 'Esta conta foi desativada nesta plataforma.',
  'auth.email_claimed_by_other_provider': ({ email, provider }) =>
    `${email} já está registrado pela forma de entrada “${provider}”.`,
  'auth.account_unavailable': () => 'Sua conta não está mais disponível.',

  // --- entrar em outro lugar ------------------------------------------------
  //
  // A entrada num provedor de pasta conectada ou num sistema conectado, não nesta plataforma.
  // *Entrada* (f.) é a palavra usada para esse ato em todo o produto, e *esta* concorda com
  // ela.

  'auth.sign_in_not_pending': () => 'Esta entrada já foi concluída, ou nunca começou por aqui.',
  'auth.sign_in_expired': () => 'Esta entrada demorou demais e expirou. Comece de novo.',

  // --- your own preferences ------------------------------------------------

  'auth.locale_not_supported': () => 'Esse não é um idioma que a plataforma fala.',
  'auth.time_zone_unknown': ({ timeZone }) => `“${timeZone}” não é um fuso horário que a plataforma conhece.`,

  // --- administering accounts and roles ------------------------------------

  'auth.account_not_found': () => 'Não existe conta com esse id.',
  'auth.account_deactivated_for_role': () =>
    'Essa conta está desativada, então um papel não daria nada a ela. Reative a conta primeiro.',
  'auth.last_administrator': () =>
    'Esta é a única pessoa administradora que resta, então esta mudança deixaria a plataforma sem ninguém ' +
    'que possa administrá-la. Dê o papel de administrador a outra pessoa primeiro.',
  'auth.cannot_remove_own_admin': () =>
    'Você não pode remover seu próprio acesso de administrador. Peça para outra pessoa administradora fazer isso.',
  'auth.settings_invalid': () => 'Essas configurações não são válidas.',
  'auth.role_already_granted': () => 'Esta conta já tem esse papel por uma concessão feita aqui.',
  'auth.role_already_denied': () => 'Esse papel já está negado para esta conta.',
  'auth.role_already_prepared': () => 'Esse papel já está aguardando para este endereço.',
  'auth.grant_not_found': () => 'Essa concessão não existe mais.',
  'auth.grant_already_in_effect': () => 'Essa concessão já está em vigor.',
  'auth.grant_not_claimed': () => 'Nenhuma conta assumiu essa concessão ainda.',
  'auth.grant_needs_another_administrator': () =>
    'Outra pessoa administradora precisa aprovar isto — você foi quem pediu.',
  'auth.prepared_grant_not_waiting': () => 'Essa concessão preparada não está mais aguardando.'
}
